import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type DialpadRetryMissingTranscriptsActive,
  type DialpadSettings,
} from "../../lib/api";
import { fmtSeconds } from "../../lib/format";

export function MinDurationSetting() {
  const qc = useQueryClient();
  const settingsQ = useQuery<DialpadSettings>({
    queryKey: ["dialpad-settings"],
    queryFn: () => api.dialpadSettings(),
    refetchInterval: false,
  });
  const setM = useMutation({
    mutationFn: (v: number) => api.setDialpadSettings(v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-settings"] });
    },
  });
  const value = settingsQ.data?.min_transcript_duration_ms;
  const opts = settingsQ.data?.allowed_values ?? [5000, 15000, 30000, 45000, 60000];
  const err = setM.error as { message?: string } | null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Transcript filter
        </span>
        <label className="text-sm text-foreground">Min call duration</label>
        <select
          value={value ?? ""}
          disabled={!settingsQ.data || setM.isPending}
          onChange={(e) => setM.mutate(Number(e.target.value))}
          className="border border-input rounded px-2 py-1 text-sm"
        >
          {value === undefined && <option value="">Loading…</option>}
          {opts.map((v) => (
            <option key={v} value={v}>
              {fmtSeconds(v / 1000)}
            </option>
          ))}
        </select>
        {setM.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
        {err && <span className="text-xs text-destructive">{err.message}</span>}
        <span className="text-xs text-muted-foreground">
          Shorter calls are kept but marked “not viable” (no transcript, excluded
          from analytics).
        </span>
      </div>
    </div>
  );
}

export function CollapsibleSection({
  title,
  subtitle,
  ariaLabel,
  defaultExpanded = false,
  autoExpand = false,
  headerActions,
  borderClassName = "border border-border",
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  ariaLabel?: string;
  defaultExpanded?: boolean;
  // When true, force-expand the section so the user can see something they
  // shouldn't miss (e.g. a sync error or an in-flight run). Once the user
  // manually toggles the section their choice wins for the rest of the
  // session — auto-expand stops fighting them.
  autoExpand?: boolean;
  headerActions?: ReactNode;
  borderClassName?: string;
  children: ReactNode;
}) {
  const [userToggled, setUserToggled] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded || autoExpand);
  useEffect(() => {
    if (autoExpand && !userToggled) {
      setExpanded(true);
    }
  }, [autoExpand, userToggled]);
  const label =
    ariaLabel ?? (typeof title === "string" ? title : "section");
  return (
    <section
      className={`rounded-lg ${borderClassName} bg-card shadow-md animate-card-enter flex flex-col`}
    >
      {/* Wraps rather than squeezes. Without `flex-wrap` the title is the only
          flexible item, so a narrow viewport collapses it to a few characters
          per line while the action button keeps its full width — the wider the
          button's label, the worse it reads. `basis-64` is what makes wrapping
          actually happen: a `flex-1` title has a 0 basis, never "wants" room,
          and so never pushes the actions to their own line. Growth is `grow`
          rather than `flex-1` on purpose — `flex-1` would set flex-basis too,
          and two utilities fighting over one property resolve by stylesheet
          order, not by the order they appear in the class list. */}
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <button
          type="button"
          onClick={() => {
            setUserToggled(true);
            setExpanded((v) => !v);
          }}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity grow basis-64 min-w-0"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`text-muted-foreground shrink-0 transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${expanded ? "rotate-90" : ""}`}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <div className="min-w-0">
            <h2 className="font-semibold text-foreground">{title}</h2>
            {subtitle != null && subtitle !== false && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {subtitle}
              </div>
            )}
          </div>
        </button>
        {headerActions && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {headerActions}
          </div>
        )}
      </header>
      <div
        className={`grid grid-cols-[minmax(0,1fr)] transition-[grid-template-rows] duration-[var(--duration-base)] ease-[var(--easing-standard)] ${expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden min-w-0">{children}</div>
      </div>
    </section>
  );
}

export type RetryTranscriptsControls = {
  onRetry: () => void;
  isPending: boolean;
  isRunning: boolean;
  active: DialpadRetryMissingTranscriptsActive | undefined;
  errorMessage: string | null;
};

export type RetryAssessmentsControls = {
  onRetry: () => void;
  isPending: boolean;
  isRunning: boolean;
  errorMessage: string | null;
};
